/*
 * jj-line-item-properties.js
 * --------------------------------------------------------------------------
 * Acumatica ERP line-item integration for the Hyper (James 3.0) theme.
 *
 * Two responsibilities:
 *
 *  1. <jj-line-item-properties> custom element
 *     Keeps the _options line-item property in sync with the selected variant.
 *     (Hyper does not re-render the product form on variant change, so the
 *     Liquid-rendered _options inputs would otherwise go stale.)
 *
 *  2. Nested-object cart submission interceptor  <-- the Acumatica fix
 *     The legacy Evergreen (Vue) theme posted line-item properties to
 *     /cart/add.js as a JSON body with _options as a NESTED OBJECT:
 *         { items: [{ id, quantity, properties: { _options: {..}, _legacy_id }}] }
 *     Acumatica's relay (a Ruby lambda using oj/shopify_api) reads _options
 *     back, parses it, and matches each Acumatica configuration Feature/Option
 *     against the _options key/values by name. See the Carpenter James docs:
 *     ConfigurationEntry errors ShopifyPropertyNotFound / ShopifyOptionNotFound
 *     fire when _options can't be parsed into a proper object.
 *
 *     Hyper submits the product form via `new FormData(this)` with
 *     bracket-notation fields (properties[_options][Finish Color]=...). Although
 *     that displays identically in the Shopify admin, it is stored/serialized
 *     differently in the orders/create webhook than the legacy nested-object
 *     submission, and the relay's parser can't read it -> configuration fails ->
 *     Acumatica rejects the order.
 *
 *     This interceptor reproduces the legacy submission EXACTLY: it builds the
 *     same JSON body (with _options as a nested object) and posts it to
 *     /cart/add.js, then hands off to Hyper's own ProductForm instance methods
 *     to refresh the cart drawer — so nothing else in the cart flow changes.
 *
 *     It ONLY activates for product forms that carry _options (configured
 *     products). Everything else stays on Hyper's native FormData path.
 * --------------------------------------------------------------------------
 */
(function () {
  /* ---------- 1. Variant -> _options sync element ---------- */
  if (!(window.customElements && customElements.get('jj-line-item-properties'))) {
    class JJLineItemProperties extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId || null;
        if (
          window.FoxTheme &&
          FoxTheme.pubsub &&
          FoxTheme.pubsub.PUB_SUB_EVENTS &&
          typeof FoxTheme.pubsub.subscribe === 'function'
        ) {
          this.unsubscribe = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            this.onVariantChange.bind(this)
          );
        }
      }
      disconnectedCallback() {
        if (typeof this.unsubscribe === 'function') this.unsubscribe();
      }
      onVariantChange(event) {
        const data = event && event.data;
        if (!data || !data.variant) return;
        if (this.sectionId && data.sectionId && String(data.sectionId) !== String(this.sectionId)) return;
        const optionValues = data.variant.options || [];
        this.querySelectorAll('input[data-jj-option-index]').forEach((input) => {
          const index = parseInt(input.dataset.jjOptionIndex, 10);
          if (Number.isNaN(index)) return;
          const value = optionValues[index];
          if (value !== undefined && value !== null) input.value = value;
        });
      }
    }
    if (window.customElements) customElements.define('jj-line-item-properties', JJLineItemProperties);
  }

  /* ---------- 2. Nested-object cart submission interceptor ---------- */
  if (window.__jjCartInterceptorInstalled) return;
  window.__jjCartInterceptorInstalled = true;

  // Build a /cart/add.js JSON body from the product form's FormData, with
  // _options rebuilt as a NESTED OBJECT. Mirrors the legacy { ..._options,
  // _legacy_id } property order.
  function jjBuildCartAddBody(formData, sections, sectionsUrl) {
    const options = {};
    const otherProps = {};
    let id = null;
    let quantity = 1;
    let sellingPlan = null;

    for (const [key, value] of formData.entries()) {
      if (key === 'id') {
        id = value;
      } else if (key === 'quantity') {
        const q = parseInt(value, 10);
        if (!Number.isNaN(q)) quantity = q;
      } else if (key === 'selling_plan') {
        if (value) sellingPlan = value;
      } else {
        const optMatch = key.match(/^properties\[_options\]\[(.+)\]$/);
        if (optMatch) {
          options[optMatch[1]] = value;
          continue;
        }
        const propMatch = key.match(/^properties\[(.+)\]$/);
        if (propMatch) otherProps[propMatch[1]] = value;
      }
    }

    if (!id) return null; // no variant resolved -> let Hyper handle it

    // Property ORDER matches the order that PASSED Acumatica (#39750), which
    // lists _options FIRST, then _legacy_id. (Order is not what Acumatica keys
    // on — a paid order passes and a $0 order fails regardless of order — but
    // mirroring the known-good order avoids it being a variable at all.)
    const properties = {};
    if (Object.keys(options).length) properties._options = options;
    Object.assign(properties, otherProps); // _legacy_id, gift-card props, etc.

    const item = { id: id, quantity: quantity, properties: properties };
    if (sellingPlan) item.selling_plan = sellingPlan;

    const body = { items: [item] };
    if (sections && sections.length) body.sections = sections;
    if (sectionsUrl) body.sections_url = sectionsUrl;
    return body;
  }

  function jjSubmit(form, event) {
    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

    form.lastSubmittedElement = event.submitter || form;
    if (typeof form.displayFormErrors === 'function') form.displayFormErrors();

    // Gather cart sections to bundle, exactly like Hyper does.
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const formData = new FormData(form);
    form.formData = formData; // handleCartError reads this.formData.get('id')

    const body = jjBuildCartAddBody(formData, sectionsToBundle, window.location.pathname);
    if (!body) {
      // Could not build a valid body — fall back to a native submit.
      form.submit();
      return;
    }

    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    };

    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('btn--loading');
    }

    fetch(`${FoxTheme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          if (typeof form.handleCartError === 'function') form.handleCartError(parsedState);
          const cartJsonSub = await (
            await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
          ).json();
          if (typeof form.updateCartState === 'function') form.updateCartState(cartJsonSub);
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
          return;
        }

        const cartJson = await (
          await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
        ).json();
        cartJson['sections'] = parsedState['sections'];

        if (typeof form.updateCartState === 'function') form.updateCartState(cartJson);
        if (typeof form.dispatchProductAddedEvent === 'function') form.dispatchProductAddedEvent(parsedState);
        if (typeof form.showCartDrawer === 'function') form.showCartDrawer();
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        if (submitButton) {
          submitButton.classList.remove('btn--loading');
          submitButton.removeAttribute('aria-disabled');
        }
      });
  }

  // Capture-phase listener fires before Hyper's own (target-phase) submit
  // handler, so we can take over the submission for configured products only.
  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.getAttribute('is') !== 'product-form') return;

      // Required globals not ready, or cart is a full page -> let Hyper handle it.
      if (!window.FoxTheme || !FoxTheme.routes || !FoxTheme.routes.cart_add_url || !FoxTheme.utils) return;
      if (FoxTheme.settings && FoxTheme.settings.cartType === 'page') return;

      // Only intercept when nested _options is present (configured products).
      if (!form.querySelector('input[name^="properties[_options]"]')) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      try {
        jjSubmit(form, event);
      } catch (e) {
        console.log('[jj] nested-options submit failed, falling back', e);
        try {
          form.submit();
        } catch (_) {}
      }
    },
    true // capture
  );
})();